import { icons } from '@gat-ship/icons'
import { appRouteDefinitions } from '@gat-ship/lib/router'
import { useRouter } from '@gat-ship/lib/router'
import { useUser } from '@gat-ship/lib/user'
import { SUPPORT_SESSION_ROUTE_QUERY_KEY } from '@gat-ship/lib/support-session'
import type {
  BaseAction,
  CreateNewSupportSessionRequest,
  SupportSession
} from '@gat-ship/types'
import { useSupportModal } from '../components/modals'
import { isSupportSessionExpired } from './support-session-utils'
import { globalUtils } from '@gat-ship/utils'
import {
  endSupportSessionRequest,
  createNewSupportSessionRequest
} from './support-api-calls'
import { useSupportPersonnelStore, useSupportSessionsStore } from '../stores'
import { accessCodes, testIds } from '@gat-ship/types'

/**
 * Type definition for support actions that can be performed.
 * Generic type T allows for different action implementations (BaseAction vs BaseActionWithClick).
 *
 * @template T - The action type (BaseAction or BaseActionWithClick)
 */
export type SupportActions<T> = {
  /** Action to start a support session */
  startSupportSession: T
  /** Action to end a support session */
  endSupportSession: T
  /** Action to view more details */
  moreDetails: T
}

export const grantSupportAccess: BaseAction = {
  label: 'Grant Support Access',
  icon: icons.support.actions.grantSupportAccess,
  testId: testIds.support.actions.grantSupportAccess
}

export const startSupportSession: BaseAction = {
  label: 'Start Support Session',
  icon: icons.support.actions.startSupportSession,
  testId: testIds.support.actions.startSupportSession
}

export const endSupportSession: BaseAction = {
  label: 'End Support Session',
  icon: icons.support.actions.endSupportSession,
  testId: testIds.support.actions.endSupportSession
}

export const moreDetails: BaseAction = {
  label: 'More Details',
  icon: icons.support.actions.moreDetails,
  testId: testIds.support.actions.moreDetails
}

export const supportActions: SupportActions<BaseAction> = {
  startSupportSession,
  endSupportSession,
  moreDetails
} as const

export type StartSupportSessionCommand = ReturnType<
  typeof useSupportCommands
>['onStartSupportSession']

export type EndSupportSessionCommand = ReturnType<
  typeof useSupportCommands
>['onEndSupportSession']

export function useSupportCommands() {
  const user = useUser()
  const supportSessionsStore = useSupportSessionsStore()
  const supportPersonnelStore = useSupportPersonnelStore()

  function mapRequestPayloadToSupportSession(
    supportSession: CreateNewSupportSessionRequest
  ): SupportSession | undefined {
    const supportPerson = supportPersonnelStore.supportPersonnel?.find(
      (person) => person.userId === supportSession.supportUserId
    )
    if (!supportPerson) {
      return undefined
    }
    const userProfile = user.userProfile.value
    if (!userProfile) {
      return undefined
    }
    return {
      sessionId: supportSession.sessionId,
      supportPersonUserId: supportPerson.userId,
      supportPersonFullName: supportPerson.fullName,
      grantedFrom: supportSession.grantFrom,
      grantedTo: supportSession.grantTo,
      grantedTenantAdmin: supportSession.grantTenantAdmin,
      ownerTenantId: userProfile.homeTenantId,
      ownerTenantName: userProfile.homeTenantName,
      requestedByUserId: userProfile.userId,
      requestedByFullName: userProfile.fullName
    }
  }

  async function onStartSupportSession(
    payload: CreateNewSupportSessionRequest,
    commandId: string
  ) {
    const result = await createNewSupportSessionRequest(payload, commandId)
    const supportSession = mapRequestPayloadToSupportSession(payload)
    if (supportSession) {
      supportSessionsStore.mySessionsDataSource.addSupportSession(
        supportSession
      )
    }
    return result
  }

  async function onEndSupportSession(sessionId: string, commandId: string) {
    const result = await endSupportSessionRequest(sessionId, commandId)
    supportSessionsStore.mySessionsDataSource.removeSupportSession(sessionId)

    return result
  }

  return {
    onStartSupportSession,
    onEndSupportSession
  }
}

export function useSupportActions() {
  const user = useUser()
  const { showModal: showCreateSupportSessionModal } = useSupportModal(
    'new-support-session'
  )
  const { showModal: showEndSupportSessionModal } = useSupportModal(
    'end-support-session'
  )
  const { showModal: showMoreDetailsModal } = useSupportModal(
    'support-session-details'
  )
  const supportCommands = useSupportCommands()

  function getGrantSupportAccessAction() {
    if (user.hasPermission(accessCodes.system.support.createSupportSession)) {
      return {
        ...grantSupportAccess,
        onClick: () => {
          showCreateSupportSessionModal({
            supportSession: {
              grantFrom: globalUtils.functions.fromNow(),
              grantTo: globalUtils.functions.fromNow({ days: 1 })
            },
            startSupportSessionCommand: supportCommands.onStartSupportSession
          })
        }
      }
    }
    return undefined
  }

  const router = useRouter()

  function getSupportActions(supportSession: SupportSession) {
    const result: Partial<SupportActions<BaseActionWithClick>> = {}
    if (!isSupportSessionExpired(supportSession)) {
      result.endSupportSession = {
        ...endSupportSession,
        onClick: () => {
          showEndSupportSessionModal({
            supportSession,
            options: {
              closeWindowOnEnd:
                user.isSupportUser.value && user.isSupportSession.value,
              useCountdown:
                user.isSupportUser.value && user.isSupportSession.value
            },
            endSupportSessionCommand: supportCommands.onEndSupportSession
          })
        }
      }
    }

    const isAssignedToSupportUser =
      supportSession.supportPersonUserId === user.id.value

    if (
      user.isSupportUser.value &&
      !isSupportSessionExpired(supportSession) &&
      isAssignedToSupportUser
    ) {
      result.startSupportSession = {
        ...startSupportSession,
        onClick: () => {
          router.openInNewTab({
            name: appRouteDefinitions.root,
            query: {
              [SUPPORT_SESSION_ROUTE_QUERY_KEY]: supportSession.sessionId
            }
          })
        }
      }
    }

    result.moreDetails = {
      ...moreDetails,
      onClick: () => {
        showMoreDetailsModal({ supportSession })
      }
    }

    return result
  }

  return {
    getSupportActions,
    getGrantSupportAccessAction
  }
}
